// Hook para crear paciente nuevo + invitación. Wrap del RPC
// `crear_paciente_invitacion` (migration 0009).
//
// Sub-decisión: NO devuelve el paciente object completo — solo el id +
// token + expiry. El caller (DashboardKine) refetchea la lista vía
// useKineDashboard para obtener el paciente nuevo en el shape correcto.

import { useState } from "react";
import { supabase } from "../client";
import type { PatientFormCreateData } from "@/app/components/patients/PatientFormDialog";

export interface CrearPacienteResult {
  pacienteId: string;
  signupToken: string;
  expiresAt: string;
}

export interface UseCrearPacienteReturn {
  crear: (data: PatientFormCreateData) => Promise<CrearPacienteResult | null>;
  loading: boolean;
  error: string | null;
  reset: () => void;
}

export function useCrearPaciente(): UseCrearPacienteReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function crear(
    data: PatientFormCreateData,
  ): Promise<CrearPacienteResult | null> {
    setLoading(true);
    setError(null);
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        "crear_paciente_invitacion",
        {
          p_nombre: data.nombre,
          p_apellido: data.apellido,
          p_email: data.email,
          p_telefono: data.telefono,
          p_notas_clinicas: data.notas_clinicas,
        },
      );

      if (rpcError) {
        console.warn("[useCrearPaciente] rpc error", rpcError);
        setError(rpcError.message);
        return null;
      }

      if (rpcData && !rpcData.ok) {
        const code = rpcData.code as string;
        const msg =
          code === "NO_AUTH"
            ? "Tu sesión expiró. Iniciá sesión de nuevo."
            : code === "NOT_KINE"
              ? "Tu cuenta no es de kinesiólogo."
              : code === "INVALID_INPUT" && rpcData.field === "email_format"
                ? "El email tiene formato inválido."
                : code === "INVALID_INPUT"
                  ? `El campo "${rpcData.field}" es inválido.`
                  : (rpcData.message as string | undefined) ??
                    "No pudimos crear el paciente. Probá de nuevo.";
        setError(msg);
        return null;
      }

      return {
        pacienteId: rpcData.paciente_id as string,
        signupToken: rpcData.signup_token as string,
        expiresAt: rpcData.expires_at as string,
      };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[useCrearPaciente] failed", e);
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }

  return {
    crear,
    loading,
    error,
    reset: () => setError(null),
  };
}
